import { FC, useState } from "react";
import { useUserStore } from "../../stores/user.store";
import { useFeedBack } from "../../providers/FeedBackProvider/FeedBackContext";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Chip from "@mui/material/Chip";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Autocomplete from "@mui/material/Autocomplete";
import Skeleton from "@mui/material/Skeleton";
import LinearProgress from "@mui/material/LinearProgress";
import BudgetEnforcementSection from "../../components/Programs/BudgetEnforcementSection";
import BudgetBadge from "../../components/Programs/BudgetBadge";
import {
	useGetPrograms,
	useCreateProgram,
	useUpdateProgram,
	useGetProgramConsumption,
} from "../../hooks/usePrograms";
import { useGetTreatmentPlans } from "../../hooks/useTreatmentPlans";
import { useGetOrganizationList } from "../../hooks/useOrganizations";
import {
	ProgramRequest,
	Program,
	EducationalContentItem,
} from "../../domain/program.interface";
import { OrganizationResponse } from "../../domain/organization.interface";
import EditIcon from "@mui/icons-material/Edit";
import { useGetActiveRolesByAccountId } from "../../hooks/useRoles";
import { useActivePayers } from "../../hooks/userPayers";
import { PayerResponse } from "../../domain/payer.interface";
import { EducationalContentSection } from "./components/EducationalContentSection";
import { formatResponseTime } from "../../utils/formatResponseTime";

const DEFAULT_NEW_CONTENT_DAYS = 7;

const ProgramsComponent: FC = () => {
	const { userInfo } = useUserStore();
	const { showSnackBar } = useFeedBack();
	const defaultAccountId =
		process.env.REACT_APP_ACCOUNT_ID || "507f1f77bcf86cd799439011";

	const [showCreateForm, setShowCreateForm] = useState(false);
	const [formData, setFormData] = useState<
		ProgramRequest & { cmRoles: string[]; additionalStaffRoles: string[] }
	>({
		accountId: defaultAccountId,
		name: "",
		type: "OUD",
		status: "ACTIVE",
		multiTreatmentPlan: false,
		treatmentPlanChangeAllowed: false,
		treatmentPlan: "",
		orgUnitId: [],
		parameters: {
			eligibility: true,
			consent: true,
			maxBeneficiaries: 100,
			durationInWeeks: 12,
			maxYearlyEarnings: 5000,
			budgetLimit: 10000,
			budgetLimitEnabled: false,
			baseIncentiveNegative: 25,
			baseIncentivePositive: 50,
			biVisitIncrementNegative: 10,
			repetitionPerSteps: 3,
			eligibilityConfirmationIntervalDays: undefined,
		},
		createdBy: userInfo?.id || "",
		cmRoles: [],
		additionalStaffRoles: [],
		payers: [],
		requirePayerPlanReviewOnReconfirm: false,
		incentiveOnDemand: false,
		onDemandIncentiveMin: undefined,
		onDemandIncentiveMax: undefined,
		features: {
			enableJournal: false,
			enableRewards: false,
			enableAutomatedNotifications: false,
			enableActivityAlerts: false,
			enableCallManagement: false,
			allowEnrollmentPause: false,
			allowIncentiveInvalidation: false,
			eventBasedRewards: false,
			journalMultiPage: false,
			journalCrisisIntervention: {
				enabled: false,
				triggerMoods: [],
				title: "",
				message: "",
			},
			callConfiguration: {
				allowCallRequests: false,
				callCooldownMinutes: 1440,
				expectedResponseHours: 24,
			},
		},
		// SIMS-4146: Activity Sidebar alert thresholds.
		activityAlerts: {
			delayedThresholdHours: 2,
			enableDelayedAlerts: true,
			enableMissedAlerts: true,
		},
		visibleTabs: {
			showWeeklyCalendar: true,
			showDailyJourney: false,
			showClinicalInformation: false,
		},
		reportConfig: {
			incentiveDisbursement: false,
			educationalEngagement: false,
			eventActivity: false,
			eventRatings: false,
		},
		educationalContent: [],
		newContentDays: DEFAULT_NEW_CONTENT_DAYS,
	});

	// State for selected organization units
	const [selectedOrgUnits, setSelectedOrgUnits] = useState<
		OrganizationResponse[]
	>([]);

	// State for selected payers
	const [selectedPayers, setSelectedPayers] = useState<PayerResponse[]>([]);

	const {
		data: programs,
		isLoading,
		error,
		refetch,
	} = useGetPrograms(defaultAccountId);
	const createMutation = useCreateProgram();
	const updateMutation = useUpdateProgram();
	const [editingProgramId, setEditingProgramId] = useState<string | null>(null);
	// SIMS-6076: live budget consumption for the program being edited (when enabled).
	const { data: budgetConsumption } = useGetProgramConsumption(
		editingProgramId,
		formData.parameters.budgetLimitEnabled ?? false,
	);

	// Fetch treatment plans for dropdown
	const { data: treatmentPlans, isLoading: treatmentPlansLoading } =
		useGetTreatmentPlans();

	// Fetch organizations for dropdown
	const { data: organizationsData, isLoading: organizationsLoading } =
		useGetOrganizationList(defaultAccountId);

	// Fetch active payers for dropdown
	const { data: payersData, isLoading: payersLoading } = useActivePayers();

	const handleInputChange = (field: string, value: string | boolean) => {
		setFormData((prev) => ({ ...prev, [field]: value }));
	};

	const handleFeatureChange = (field: string, value: boolean) => {
		setFormData((prev) => ({
			...prev,
			features: { ...(prev.features ?? {}), [field]: value },
		}));
	};

	// SIMS-4146: update the Activity Sidebar alert config.
	const handleActivityAlertChange = (
		field:
			| "delayedThresholdHours"
			| "enableDelayedAlerts"
			| "enableMissedAlerts",
		value: number | boolean,
	) => {
		setFormData((prev) => ({
			...prev,
			activityAlerts: { ...(prev.activityAlerts ?? {}), [field]: value },
		}));
	};

	const handleCrisisInterventionChange = (
		field: "enabled" | "title" | "message" | "triggerMoods",
		value: boolean | string | string[],
	) => {
		setFormData((prev) => ({
			...prev,
			features: {
				...(prev.features ?? {}),
				journalCrisisIntervention: {
					...(prev.features?.journalCrisisIntervention ?? {}),
					[field]: value,
				},
			},
		}));
	};

	const handleCallConfigurationChange = (
		field:
			| "allowCallRequests"
			| "callCooldownMinutes"
			| "expectedResponseHours",
		value: boolean | number | undefined,
	) => {
		setFormData((prev) => ({
			...prev,
			features: {
				...(prev.features ?? {}),
				callConfiguration: {
					...(prev.features?.callConfiguration ?? {}),
					[field]: value,
				},
			},
		}));
	};

	const handleVisibleTabsChange = (field: string, value: boolean) => {
		setFormData((prev) => ({
			...prev,
			visibleTabs: { ...(prev.visibleTabs ?? {}), [field]: value },
		}));
	};

	const handleReportConfigChange = (field: string, value: boolean) => {
		setFormData((prev) => ({
			...prev,
			reportConfig: { ...(prev.reportConfig ?? {}), [field]: value },
		}));
	};

	const handleEducationalContentChange = (items: EducationalContentItem[]) => {
		setFormData((prev) => ({ ...prev, educationalContent: items }));
	};

	const handleNewContentDaysChange = (value: number) => {
		setFormData((prev) => ({ ...prev, newContentDays: value }));
	};

	const handleParameterChange = (
		field: string,
		value: number | boolean | string,
	) => {
		setFormData((prev) => {
			const updatedParameters = { ...prev.parameters, [field]: value };
			// SIMS-6067: enabling budget enforcement seeds sensible defaults so the
			// required threshold/mode inputs are controlled.
			if (field === "budgetLimitEnabled" && value === true) {
				if (updatedParameters.budgetLimitThreshold === undefined) {
					updatedParameters.budgetLimitThreshold = 80;
				}
				if (updatedParameters.budgetLimitEnforcementMode === undefined) {
					updatedParameters.budgetLimitEnforcementMode = "BLOCK";
				}
			}
			if (field === "eligibility" && value === false) {
				updatedParameters.eligibilityConfirmationIntervalDays = undefined;
				return {
					...prev,
					parameters: updatedParameters,
					requirePayerPlanReviewOnReconfirm: false,
				};
			}
			return { ...prev, parameters: updatedParameters };
		});
	};

	const resetForm = () => {
		setSelectedOrgUnits([]);
		setSelectedPayers([]);
		setFormData({
			accountId: defaultAccountId,
			name: "",
			type: "OUD",
			status: "ACTIVE",
			multiTreatmentPlan: false,
			treatmentPlanChangeAllowed: false,
			treatmentPlan: "",
			orgUnitId: [],
			parameters: {
				eligibility: true,
				consent: true,
				maxBeneficiaries: 100,
				durationInWeeks: 12,
				maxYearlyEarnings: 5000,
				budgetLimit: 10000,
				budgetLimitEnabled: false,
				baseIncentiveNegative: 25,
				baseIncentivePositive: 50,
				biVisitIncrementNegative: 10,
				repetitionPerSteps: 3,
			},
			createdBy: userInfo?.id || "",
			cmRoles: [],
			additionalStaffRoles: [],
			payers: [],
			requirePayerPlanReviewOnReconfirm: false,
			incentiveOnDemand: false,
			onDemandIncentiveMin: undefined,
			onDemandIncentiveMax: undefined,
			features: {
				enableJournal: false,
				enableAutomatedNotifications: false,
				enableActivityAlerts: false,
				enableCallManagement: false,
				allowEnrollmentPause: false,
				allowIncentiveInvalidation: false,
				eventBasedRewards: false,
				journalMultiPage: false,
				journalCrisisIntervention: {
					enabled: false,
					triggerMoods: [],
					title: "",
					message: "",
				},
				callConfiguration: {
					allowCallRequests: false,
					callCooldownMinutes: 1440,
					expectedResponseHours: 24,
				},
			},
			visibleTabs: {
				showWeeklyCalendar: true,
				showDailyJourney: false,
				showClinicalInformation: false,
			},
			reportConfig: {
				incentiveDisbursement: false,
				educationalEngagement: false,
				eventActivity: false,
				eventRatings: false,
			},
			educationalContent: [],
			newContentDays: DEFAULT_NEW_CONTENT_DAYS,
		});
	};

	const handleEditProgram = (program: Program) => {
		setShowCreateForm(true);
		setEditingProgramId(program._id);
		setFormData({
			accountId: program.accountId,
			name: program.name,
			type: program.type,
			status: program.status,
			multiTreatmentPlan: program.multiTreatmentPlan,
			treatmentPlanChangeAllowed: program.treatmentPlanChangeAllowed,
			treatmentPlan: program.treatmentPlan || "",
			orgUnitId: program.orgUnitId || [],
			parameters: normalizeParameters(program.parameters),
			createdBy: program.createdBy,
			cmRoles: program.cmRoles || [],
			additionalStaffRoles: program.additionalStaffRoles || [],
			payers: program.payers || [],
			requirePayerPlanReviewOnReconfirm:
				program.requirePayerPlanReviewOnReconfirm || false,
			incentiveOnDemand: program.incentiveOnDemand || false,
			onDemandIncentiveMin: program.onDemandIncentiveMin,
			onDemandIncentiveMax: program.onDemandIncentiveMax,
			features: {
				enableJournal: program.features?.enableJournal ?? false,
				enableRewards: program.features?.enableRewards ?? false,
				enableAutomatedNotifications:
					program.features?.enableAutomatedNotifications ?? false,
				enableActivityAlerts: program.features?.enableActivityAlerts ?? false,
				enableCallManagement: program.features?.enableCallManagement ?? false,
				// SIMS-4146: preserve undefined (do NOT `?? false`) so the backend's
				// hybrid auto-detect still applies to programs the admin never toggled.
				activitySidebar: program.features?.activitySidebar,
				realtimeAlerts: program.features?.realtimeAlerts,
				allowEnrollmentPause: program.features?.allowEnrollmentPause ?? false,
				allowIncentiveInvalidation:
					program.features?.allowIncentiveInvalidation ?? false,
				eventBasedRewards: program.features?.eventBasedRewards ?? false,
				journalMultiPage: program.features?.journalMultiPage ?? false,
				journalCrisisIntervention: {
					enabled:
						program.features?.journalCrisisIntervention?.enabled ?? false,
					triggerMoods:
						program.features?.journalCrisisIntervention?.triggerMoods ?? [],
					title: program.features?.journalCrisisIntervention?.title ?? "",
					message: program.features?.journalCrisisIntervention?.message ?? "",
				},
				callConfiguration: {
					allowCallRequests:
						program.features?.callConfiguration?.allowCallRequests ?? false,
					callCooldownMinutes:
						program.features?.callConfiguration?.callCooldownMinutes ?? 1440,
					expectedResponseHours:
						program.features?.callConfiguration?.expectedResponseHours ?? 24,
				},
			},
			activityAlerts: {
				delayedThresholdHours:
					program.activityAlerts?.delayedThresholdHours ?? 2,
				enableDelayedAlerts:
					program.activityAlerts?.enableDelayedAlerts ?? true,
				enableMissedAlerts: program.activityAlerts?.enableMissedAlerts ?? true,
			},
			visibleTabs: {
				showWeeklyCalendar: program.visibleTabs?.showWeeklyCalendar ?? true,
				showDailyJourney: program.visibleTabs?.showDailyJourney ?? false,
				showClinicalInformation:
					program.visibleTabs?.showClinicalInformation ?? false,
			},
			reportConfig: {
				incentiveDisbursement:
					program.reportConfig?.incentiveDisbursement ?? false,
				educationalEngagement:
					program.reportConfig?.educationalEngagement ?? false,
				eventActivity: program.reportConfig?.eventActivity ?? false,
				eventRatings: program.reportConfig?.eventRatings ?? false,
			},
			educationalContent: program.educationalContent ?? [],
			newContentDays: program.newContentDays ?? DEFAULT_NEW_CONTENT_DAYS,
		});
		// Set selected org units for Autocomplete
		if (organizationsData) {
			setSelectedOrgUnits(
				organizationsData.filter((org) =>
					(program.orgUnitId || []).includes(org._id),
				),
			);
		}
		// Set selected payers for Autocomplete
		if (payersData) {
			setSelectedPayers(
				payersData.filter((payer) =>
					(program.payers || []).includes(payer._id),
				),
			);
		}
	};
	// Fetch roles for dropdowns
	const { data: rolesData = [], isLoading: rolesLoading } =
		useGetActiveRolesByAccountId();

	const handleSubmit = async () => {
		if (
			formData.parameters.eligibility &&
			(!formData.parameters.eligibilityConfirmationIntervalDays ||
				formData.parameters.eligibilityConfirmationIntervalDays < 1)
		) {
			showSnackBar(
				"Eligibility Reconfirmation Interval (days) is required and must be at least 1.",
				"error",
			);
			return;
		}

		// SIMS-6067 AC4: when budget enforcement is enabled, limit + threshold + mode are required.
		if (
			formData.parameters.budgetLimitEnabled &&
			(!formData.parameters.budgetLimit ||
				!formData.parameters.budgetLimitThreshold ||
				!formData.parameters.budgetLimitEnforcementMode)
		) {
			showSnackBar(
				"Budget limit, threshold, and enforcement mode are required when budget enforcement is enabled.",
				"error",
			);
			return;
		}

		// Remove _id from parameters before sending
		const cleanParameters = { ...formData.parameters };
		delete cleanParameters._id;

		// Clean durationConfig if it exists
		const cleanDurationConfig = cleanParameters.durationConfig
			? // eslint-disable-next-line @typescript-eslint/no-explicit-any
				cleanParameters.durationConfig.map((d: any) => {
					const cleanD = { ...d };
					delete cleanD._id;
					return cleanD;
				})
			: [];

		const cleanFormData = {
			...formData,
			parameters: {
				...cleanParameters,
				...(cleanDurationConfig.length > 0 && {
					durationConfig: cleanDurationConfig,
				}),
			},
		};

		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const sanitizedData: any = { ...cleanFormData };

		// Coerce on-demand incentives to number (empty → drop, string → Number)
		if (
			sanitizedData.onDemandIncentiveMin === undefined ||
			sanitizedData.onDemandIncentiveMin === "" ||
			sanitizedData.onDemandIncentiveMin === null
		) {
			delete sanitizedData.onDemandIncentiveMin;
		} else {
			sanitizedData.onDemandIncentiveMin = Number(
				sanitizedData.onDemandIncentiveMin,
			);
		}
		if (
			sanitizedData.onDemandIncentiveMax === undefined ||
			sanitizedData.onDemandIncentiveMax === "" ||
			sanitizedData.onDemandIncentiveMax === null
		) {
			delete sanitizedData.onDemandIncentiveMax;
		} else {
			sanitizedData.onDemandIncentiveMax = Number(
				sanitizedData.onDemandIncentiveMax,
			);
		}

		// Empty strings on ObjectId fields fail @IsObjectId — drop them
		if (!sanitizedData.treatmentPlan) {
			delete sanitizedData.treatmentPlan;
		}

		try {
			if (editingProgramId) {
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				const updateData: any = { ...sanitizedData };
				delete updateData.createdBy;
				updateData.updatedBy = userInfo?.id || "";

				await updateMutation.mutateAsync({
					programId: editingProgramId,
					programData: updateData,
				});
				setEditingProgramId(null);
			} else {
				// Create
				await createMutation.mutateAsync({
					accountId: defaultAccountId,
					programData: sanitizedData,
				});
			}
			setShowCreateForm(false);
		} catch (err) {
			const errorMsg =
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				(err as any)?.message?.join?.(", ") ||
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				(err as any)?.message ||
				"An error occurred while saving the program";
			showSnackBar(errorMsg, "error");
		}
		resetForm();
		refetch();
	};

	if (error)
		return <Typography color="error">Error loading programs</Typography>;

	const renderProgramSkeletons = (count = 6) =>
		Array.from({ length: count }).map((_, i) => (
			<Grid item xs={12} md={6} lg={4} key={`skeleton-${i}`}>
				<Card>
					<CardContent>
						<Box
							display="flex"
							justifyContent="space-between"
							alignItems="center"
							mb={2}
						>
							<Skeleton variant="text" width="60%" height={32} />
							<Box display="flex" gap={1}>
								<Skeleton variant="rounded" width={56} height={24} />
								<Skeleton variant="rounded" width={56} height={24} />
								<Skeleton variant="rounded" width={64} height={30} />
							</Box>
						</Box>
						<Skeleton variant="text" width="70%" />
						<Skeleton variant="text" width="50%" />
						<Skeleton variant="text" width="80%" />
						<Skeleton variant="text" width="40%" sx={{ mt: 2 }} />
						<Skeleton variant="text" width="55%" />
					</CardContent>
				</Card>
			</Grid>
		));

	// Helper function to get treatment plan name by ID
	const getTreatmentPlanName = (treatmentPlanId?: string): string => {
		if (!treatmentPlanId) return "None";
		const plan = treatmentPlans?.find((tp) => tp._id === treatmentPlanId);
		return plan?.name || `Treatment Plan ${treatmentPlanId.slice(-8)}`;
	};

	return (
		<Box sx={{ padding: 3 }}>
			<Box
				display="flex"
				justifyContent="space-between"
				alignItems="center"
				mb={3}
			>
				<Typography variant="h4" component="h1">
					Program Management
				</Typography>
				<Button
					variant="contained"
					onClick={() => setShowCreateForm(!showCreateForm)}
				>
					{showCreateForm ? "Cancel" : "Create New Program"}
				</Button>
			</Box>

			{showCreateForm && (
				<Paper
					sx={{ padding: 3, marginBottom: 3, position: "relative" }}
					aria-busy={createMutation.isPending || updateMutation.isPending}
				>
					{(createMutation.isPending || updateMutation.isPending) && (
						<LinearProgress
							sx={{
								position: "absolute",
								top: 0,
								left: 0,
								right: 0,
								borderTopLeftRadius: 4,
								borderTopRightRadius: 4,
							}}
						/>
					)}
					<Typography variant="h6" gutterBottom>
						{editingProgramId ? "Edit Program" : "Create New Program"}
					</Typography>
					<Grid container spacing={3}>
						<Grid item xs={12} md={6}>
							<TextField
								fullWidth
								label="Program Name"
								value={formData.name}
								onChange={(e) => handleInputChange("name", e.target.value)}
								required
							/>
						</Grid>
						<Grid item xs={12} md={6}>
							<FormControl fullWidth>
								<InputLabel>Type</InputLabel>
								<Select
									value={formData.type || "Legacy"}
									label="Type"
									onChange={(e) => handleInputChange("type", e.target.value)}
								>
									<MenuItem value="LEGACY">Legacy</MenuItem>
									<MenuItem value="OUD">OUD</MenuItem>
									<MenuItem value="STIM">Stim</MenuItem>
									<MenuItem value="OTHER">Other</MenuItem>
								</Select>
							</FormControl>
						</Grid>
						<Grid item xs={12} md={6}>
							<FormControl fullWidth>
								<InputLabel>Status</InputLabel>
								<Select
									value={formData.status}
									label="Status"
									onChange={(e) => handleInputChange("status", e.target.value)}
								>
									<MenuItem value="ACTIVE">Active</MenuItem>
									<MenuItem value="INACTIVE">Inactive</MenuItem>
								</Select>
							</FormControl>
						</Grid>
						{/* Created By field removed: now set automatically from userInfo */}
						<Grid item xs={12} md={6}>
							<FormControl fullWidth>
								<InputLabel>Treatment Plan (Optional)</InputLabel>
								<Select
									value={formData.treatmentPlan}
									label="Treatment Plan (Optional)"
									onChange={(e) =>
										handleInputChange("treatmentPlan", e.target.value)
									}
									disabled={treatmentPlansLoading}
								>
									<MenuItem value="">
										<em>None</em>
									</MenuItem>
									{treatmentPlans?.map((treatmentPlan) => (
										<MenuItem key={treatmentPlan._id} value={treatmentPlan._id}>
											{treatmentPlan.name ||
												`Treatment Plan ${treatmentPlan._id.slice(-8)}`}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						</Grid>

						<Grid item xs={12} md={6}>
							<FormControlLabel
								control={
									<Checkbox
										checked={formData.multiTreatmentPlan}
										onChange={(e) =>
											handleInputChange("multiTreatmentPlan", e.target.checked)
										}
									/>
								}
								label="Multi Treatment Plan"
							/>
						</Grid>
						<Grid item xs={12} md={6}>
							<FormControlLabel
								control={
									<Checkbox
										checked={formData.treatmentPlanChangeAllowed}
										onChange={(e) =>
											handleInputChange(
												"treatmentPlanChangeAllowed",
												e.target.checked,
											)
										}
									/>
								}
								label="Treatment Plan Change Allowed"
							/>
						</Grid>
						{/* Features Card */}
						<Grid item xs={12} md={6}>
							<Card variant="outlined" sx={{ height: "100%" }}>
								<CardContent>
									<Typography variant="h6" gutterBottom>
										Features
									</Typography>
									<Typography
										variant="body2"
										color="text.secondary"
										sx={{ mb: 1 }}
									>
										Enable workflow capabilities for this program.
									</Typography>
									<Grid container>
										<Grid item xs={12} sm={6}>
											<FormControlLabel
												control={
													<Checkbox
														checked={formData.features?.enableJournal ?? false}
														onChange={(e) =>
															handleFeatureChange(
																"enableJournal",
																e.target.checked,
															)
														}
													/>
												}
												label="Enable Journal (Mobile)"
											/>
										</Grid>
										<Grid item xs={12} sm={6}>
											<FormControlLabel
												control={
													<Checkbox
														checked={formData.features?.enableRewards ?? false}
														onChange={(e) =>
															handleFeatureChange(
																"enableRewards",
																e.target.checked,
															)
														}
													/>
												}
												label="Enable Rewards (Mobile)"
											/>
										</Grid>
										<Grid item xs={12} sm={6}>
											<FormControlLabel
												control={
													<Checkbox
														checked={
															formData.features?.enableAutomatedNotifications ??
															false
														}
														onChange={(e) =>
															handleFeatureChange(
																"enableAutomatedNotifications",
																e.target.checked,
															)
														}
													/>
												}
												label="Enable Automated Notifications"
											/>
										</Grid>
										<Grid item xs={12} sm={6}>
											<FormControlLabel
												control={
													<Checkbox
														checked={
															formData.features?.enableActivityAlerts ?? false
														}
														onChange={(e) =>
															handleFeatureChange(
																"enableActivityAlerts",
																e.target.checked,
															)
														}
													/>
												}
												label="Enable Activity Alerts"
											/>
										</Grid>
										<Grid item xs={12} sm={6}>
											<FormControlLabel
												control={
													<Checkbox
														checked={
															formData.features?.enableCallManagement ?? false
														}
														onChange={(e) =>
															handleFeatureChange(
																"enableCallManagement",
																e.target.checked,
															)
														}
													/>
												}
												label="Enable Call Management"
											/>
										</Grid>
										{/* SIMS-4146: Daily Workflow Features — Activity Sidebar */}
										<Grid item xs={12} sm={6}>
											<FormControlLabel
												control={
													<Checkbox
														checked={
															formData.features?.activitySidebar ?? false
														}
														onChange={(e) =>
															handleFeatureChange(
																"activitySidebar",
																e.target.checked,
															)
														}
													/>
												}
												label="Enable Activity Sidebar"
											/>
										</Grid>
										<Grid item xs={12} sm={6}>
											<FormControlLabel
												control={
													<Checkbox
														checked={formData.features?.realtimeAlerts ?? false}
														onChange={(e) =>
															handleFeatureChange(
																"realtimeAlerts",
																e.target.checked,
															)
														}
													/>
												}
												label="Enable Real-Time Alerts"
											/>
										</Grid>
										{/* SIMS-4146: Activity Alerts config — shown only when the sidebar is on */}
										{formData.features?.activitySidebar && (
											<>
												<Grid item xs={12} sm={6}>
													<FormControl fullWidth size="small">
														<InputLabel id="delayed-threshold-label">
															Delayed Activity Threshold (hours)
														</InputLabel>
														<Select
															labelId="delayed-threshold-label"
															label="Delayed Activity Threshold (hours)"
															value={
																formData.activityAlerts
																	?.delayedThresholdHours ?? 2
															}
															onChange={(e) =>
																handleActivityAlertChange(
																	"delayedThresholdHours",
																	Number(e.target.value),
																)
															}
														>
															{[0.5, 1, 2, 4, 6, 8, 12, 24].map((h) => (
																<MenuItem key={h} value={h}>
																	{h}
																</MenuItem>
															))}
														</Select>
													</FormControl>
												</Grid>
												<Grid item xs={12} sm={6}>
													<FormControlLabel
														control={
															<Checkbox
																checked={
																	formData.activityAlerts
																		?.enableDelayedAlerts ?? true
																}
																onChange={(e) =>
																	handleActivityAlertChange(
																		"enableDelayedAlerts",
																		e.target.checked,
																	)
																}
															/>
														}
														label="Enable Delayed Alerts"
													/>
												</Grid>
												<Grid item xs={12} sm={6}>
													<FormControlLabel
														control={
															<Checkbox
																checked={
																	formData.activityAlerts?.enableMissedAlerts ??
																	true
																}
																onChange={(e) =>
																	handleActivityAlertChange(
																		"enableMissedAlerts",
																		e.target.checked,
																	)
																}
															/>
														}
														label="Enable Missed Alerts"
													/>
												</Grid>
											</>
										)}
										<Grid item xs={12} sm={6}>
											<FormControlLabel
												control={
													<Checkbox
														checked={
															formData.features?.allowEnrollmentPause ?? false
														}
														onChange={(e) =>
															handleFeatureChange(
																"allowEnrollmentPause",
																e.target.checked,
															)
														}
													/>
												}
												label="Allow Enrollment Pause"
											/>
										</Grid>
										<Grid item xs={12} sm={6}>
											<FormControlLabel
												control={
													<Checkbox
														checked={
															formData.features?.allowIncentiveInvalidation ??
															false
														}
														onChange={(e) =>
															handleFeatureChange(
																"allowIncentiveInvalidation",
																e.target.checked,
															)
														}
													/>
												}
												label="Allow Incentive Invalidation"
											/>
										</Grid>
										<Grid item xs={12} sm={6}>
											<FormControlLabel
												control={
													<Checkbox
														checked={
															formData.features?.eventBasedRewards ?? false
														}
														onChange={(e) =>
															handleFeatureChange(
																"eventBasedRewards",
																e.target.checked,
															)
														}
													/>
												}
												label="Event-Based Rewards"
											/>
										</Grid>
										<Grid item xs={12} sm={6}>
											<FormControlLabel
												control={
													<Checkbox
														checked={
															formData.features?.journalMultiPage ?? false
														}
														onChange={(e) =>
															handleFeatureChange(
																"journalMultiPage",
																e.target.checked,
															)
														}
													/>
												}
												label="Journal Multi-Page"
											/>
										</Grid>
										<Grid item xs={12}>
											<FormControlLabel
												control={
													<Checkbox
														checked={
															formData.features?.journalCrisisIntervention
																?.enabled ?? false
														}
														onChange={(e) =>
															handleCrisisInterventionChange(
																"enabled",
																e.target.checked,
															)
														}
													/>
												}
												label="Enable Journal Crisis Intervention"
											/>
										</Grid>
										{formData.features?.journalCrisisIntervention?.enabled && (
											<>
												<Grid item xs={12}>
													<TextField
														fullWidth
														size="small"
														label="Crisis Trigger Moods (comma-separated)"
														value={(
															formData.features?.journalCrisisIntervention
																?.triggerMoods ?? []
														).join(", ")}
														onChange={(e) =>
															handleCrisisInterventionChange(
																"triggerMoods",
																e.target.value
																	.split(",")
																	.map((s) => s.trim())
																	.filter(Boolean),
															)
														}
														helperText="E.g. sad, anxious, hopeless"
													/>
												</Grid>
												<Grid item xs={12} sm={6}>
													<TextField
														fullWidth
														size="small"
														label="Crisis Title"
														value={
															formData.features?.journalCrisisIntervention
																?.title ?? ""
														}
														onChange={(e) =>
															handleCrisisInterventionChange(
																"title",
																e.target.value,
															)
														}
													/>
												</Grid>
												<Grid item xs={12} sm={6}>
													<TextField
														fullWidth
														size="small"
														label="Crisis Message"
														multiline
														minRows={2}
														value={
															formData.features?.journalCrisisIntervention
																?.message ?? ""
														}
														onChange={(e) =>
															handleCrisisInterventionChange(
																"message",
																e.target.value,
															)
														}
													/>
												</Grid>
											</>
										)}
										{formData.features?.enableCallManagement && (
											<>
												<Grid item xs={12}>
													<Typography variant="subtitle2" gutterBottom>
														Call Management Configuration
													</Typography>
												</Grid>
												<Grid item xs={12}>
													<FormControlLabel
														control={
															<Checkbox
																checked={
																	formData.features?.callConfiguration
																		?.allowCallRequests ?? false
																}
																onChange={(e) =>
																	handleCallConfigurationChange(
																		"allowCallRequests",
																		e.target.checked,
																	)
																}
															/>
														}
														label="Allow Participant Requests"
													/>
												</Grid>
												{formData.features?.callConfiguration
													?.allowCallRequests && (
													<>
														<Grid item xs={12} sm={6}>
															<TextField
																fullWidth
																size="small"
																type="number"
																label="Call Cooldown (minutes)"
																value={
																	formData.features?.callConfiguration
																		?.callCooldownMinutes ?? ""
																}
																onChange={(e) =>
																	handleCallConfigurationChange(
																		"callCooldownMinutes",
																		e.target.value === ""
																			? undefined
																			: Number(e.target.value),
																	)
																}
																inputProps={{ min: 15, max: 10080 }}
																helperText="Between 15 minutes and 7 days (10080 min)"
															/>
														</Grid>
														<Grid item xs={12} sm={6}>
															<TextField
																fullWidth
																size="small"
																type="number"
																label="Expected Response Time (hours)"
																value={
																	formData.features?.callConfiguration
																		?.expectedResponseHours ?? ""
																}
																onChange={(e) =>
																	handleCallConfigurationChange(
																		"expectedResponseHours",
																		e.target.value === ""
																			? undefined
																			: Number(e.target.value),
																	)
																}
																inputProps={{ min: 1, max: 168 }}
																helperText="Between 1 hour and 7 days (168 h)"
															/>
														</Grid>
														<Grid item xs={12}>
															<Typography
																variant="body2"
																color="text.secondary"
															>
																Participants will see:{" "}
																{formatResponseTime(
																	formData.features?.callConfiguration
																		?.expectedResponseHours ?? 24,
																)}
															</Typography>
														</Grid>
													</>
												)}
											</>
										)}
									</Grid>
								</CardContent>
							</Card>
						</Grid>

						{/* Visible Tabs Card */}
						<Grid item xs={12} md={6}>
							<Card variant="outlined" sx={{ height: "100%" }}>
								<CardContent>
									<Typography variant="h6" gutterBottom>
										Visible Tabs
									</Typography>
									<Typography
										variant="body2"
										color="text.secondary"
										sx={{ mb: 1 }}
									>
										Choose which tabs are visible on the participant view.
									</Typography>
									<Grid container>
										<Grid item xs={12}>
											<FormControlLabel
												control={
													<Checkbox
														checked={
															formData.visibleTabs?.showWeeklyCalendar ?? true
														}
														onChange={(e) =>
															handleVisibleTabsChange(
																"showWeeklyCalendar",
																e.target.checked,
															)
														}
													/>
												}
												label="Show Weekly Calendar"
											/>
										</Grid>
										<Grid item xs={12}>
											<FormControlLabel
												control={
													<Checkbox
														checked={
															formData.visibleTabs?.showDailyJourney ?? false
														}
														onChange={(e) =>
															handleVisibleTabsChange(
																"showDailyJourney",
																e.target.checked,
															)
														}
													/>
												}
												label="Show Daily Journey"
											/>
										</Grid>
										<Grid item xs={12}>
											<FormControlLabel
												control={
													<Checkbox
														checked={
															formData.visibleTabs?.showClinicalInformation ??
															false
														}
														onChange={(e) =>
															handleVisibleTabsChange(
																"showClinicalInformation",
																e.target.checked,
															)
														}
													/>
												}
												label="Show Clinical Information"
											/>
										</Grid>
									</Grid>
								</CardContent>
							</Card>
						</Grid>

						{/* Reports Card */}
						<Grid item xs={12}>
							<Card variant="outlined">
								<CardContent>
									<Typography variant="h6" gutterBottom>
										Reports
									</Typography>
									<Typography
										variant="body2"
										color="text.secondary"
										sx={{ mb: 2 }}
									>
										Choose which sub-reports are available in the consolidated
										report for this program. Enrollment summary is always
										included.
									</Typography>
									<Grid container spacing={1}>
										<Grid item xs={12} md={6}>
											<FormControlLabel
												control={<Checkbox checked disabled />}
												label="Enrollment summary"
											/>
											<Typography
												variant="caption"
												display="block"
												color="text.secondary"
												sx={{ ml: 4 }}
											>
												Beneficiary enrollment status overview. Always enabled.
											</Typography>
										</Grid>
										<Grid item xs={12} md={6}>
											<FormControlLabel
												control={
													<Checkbox
														checked={
															formData.reportConfig?.incentiveDisbursement ??
															false
														}
														onChange={(e) =>
															handleReportConfigChange(
																"incentiveDisbursement",
																e.target.checked,
															)
														}
													/>
												}
												label="Incentive disbursement"
											/>
											<Typography
												variant="caption"
												display="block"
												color="text.secondary"
												sx={{ ml: 4 }}
											>
												Earned, redeemed and banked incentive totals per
												beneficiary.
											</Typography>
										</Grid>
										<Grid item xs={12} md={6}>
											<FormControlLabel
												control={
													<Checkbox
														checked={
															formData.reportConfig?.educationalEngagement ??
															false
														}
														onChange={(e) =>
															handleReportConfigChange(
																"educationalEngagement",
																e.target.checked,
															)
														}
													/>
												}
												label="Educational engagement"
											/>
											<Typography
												variant="caption"
												display="block"
												color="text.secondary"
												sx={{ ml: 4 }}
											>
												Content views and completion. Requires educational
												content configured for the program.
											</Typography>
										</Grid>
										<Grid item xs={12} md={6}>
											<FormControlLabel
												control={
													<Checkbox
														checked={
															formData.reportConfig?.eventActivity ?? false
														}
														onChange={(e) =>
															handleReportConfigChange(
																"eventActivity",
																e.target.checked,
															)
														}
													/>
												}
												label="Event activity"
											/>
											<Typography
												variant="caption"
												display="block"
												color="text.secondary"
												sx={{ ml: 4 }}
											>
												Day-by-day pivot of event submissions per enrollment.
											</Typography>
										</Grid>
										<Grid item xs={12} md={6}>
											<FormControlLabel
												control={
													<Checkbox
														checked={
															formData.reportConfig?.eventRatings ?? false
														}
														onChange={(e) =>
															handleReportConfigChange(
																"eventRatings",
																e.target.checked,
															)
														}
													/>
												}
												label="Event ratings"
											/>
											<Typography
												variant="caption"
												display="block"
												color="text.secondary"
												sx={{ ml: 4 }}
											>
												Average and per-question scoring. Requires at least one
												event with scoring configured.
											</Typography>
										</Grid>
									</Grid>
								</CardContent>
							</Card>
						</Grid>

						{/* Parameters Card */}
						<Grid item xs={12}>
							<Card variant="outlined">
								<CardContent>
									<Typography variant="h6" gutterBottom>
										Program Parameters
									</Typography>
									<Typography
										variant="body2"
										color="text.secondary"
										sx={{ mb: 2 }}
									>
										Eligibility, consent, incentives, duration, and budget
										settings.
									</Typography>
									<Grid container spacing={2}>
										<Grid item xs={12} md={6}>
											<FormControlLabel
												control={
													<Checkbox
														checked={formData.parameters.eligibility}
														onChange={(e) =>
															handleParameterChange(
																"eligibility",
																e.target.checked,
															)
														}
													/>
												}
												label="Eligibility Required"
											/>
										</Grid>
										{formData.parameters.eligibility && (
											<>
												<Grid item xs={12} md={6}>
													<TextField
														type="number"
														label="Eligibility Reconfirmation Interval (days)"
														value={
															formData.parameters
																.eligibilityConfirmationIntervalDays ?? ""
														}
														onChange={(e) => {
															const raw = e.target.value;
															if (raw === "") {
																handleParameterChange(
																	"eligibilityConfirmationIntervalDays",
																	undefined as unknown as number,
																);
																return;
															}
															const parsed = parseInt(raw, 10);
															if (
																!isNaN(parsed) &&
																parsed >= 1 &&
																parsed <= 100
															) {
																handleParameterChange(
																	"eligibilityConfirmationIntervalDays",
																	parsed,
																);
															}
														}}
														inputProps={{ min: 1, max: 100 }}
														required
														error={
															formData.parameters.eligibility &&
															!formData.parameters
																.eligibilityConfirmationIntervalDays
														}
														fullWidth
														size="small"
													/>
												</Grid>
												<Grid item xs={12} md={6}>
													<FormControlLabel
														control={
															<Checkbox
																checked={
																	formData.requirePayerPlanReviewOnReconfirm ||
																	false
																}
																onChange={(e) =>
																	handleInputChange(
																		"requirePayerPlanReviewOnReconfirm",
																		e.target.checked,
																	)
																}
															/>
														}
														label="Require Payer Plan Review on Reconfirmation"
													/>
												</Grid>
											</>
										)}
										<Grid item xs={12} md={6}>
											<FormControlLabel
												control={
													<Checkbox
														checked={formData.parameters.consent}
														onChange={(e) =>
															handleParameterChange("consent", e.target.checked)
														}
													/>
												}
												label="Consent Required"
											/>
										</Grid>

										<Grid item xs={12} md={6}>
											<FormControlLabel
												control={
													<Checkbox
														checked={formData.incentiveOnDemand || false}
														onChange={(e) =>
															handleInputChange(
																"incentiveOnDemand",
																e.target.checked,
															)
														}
													/>
												}
												label="On Demand Incentive"
											/>
										</Grid>
										{formData.incentiveOnDemand && (
											<>
												<Grid item xs={12} md={6}>
													<TextField
														fullWidth
														label="On Demand Min Amount"
														type="number"
														value={formData.onDemandIncentiveMin ?? ""}
														onChange={(e) =>
															handleInputChange(
																"onDemandIncentiveMin",
																e.target.value,
															)
														}
														inputProps={{ min: 0, step: "0.01" }}
													/>
												</Grid>
												<Grid item xs={12} md={6}>
													<TextField
														fullWidth
														label="On Demand Max Amount"
														type="number"
														value={formData.onDemandIncentiveMax ?? ""}
														onChange={(e) =>
															handleInputChange(
																"onDemandIncentiveMax",
																e.target.value,
															)
														}
														inputProps={{ min: 0, step: "0.01" }}
													/>
												</Grid>
											</>
										)}

										<Grid item xs={12} md={6}>
											<TextField
												fullWidth
												label="Max Beneficiaries"
												type="number"
												value={formData.parameters.maxBeneficiaries}
												onChange={(e) =>
													handleParameterChange(
														"maxBeneficiaries",
														parseInt(e.target.value),
													)
												}
												required
											/>
										</Grid>
										<Grid item xs={12} md={6}>
											<TextField
												fullWidth
												label="Duration (Weeks)"
												type="number"
												value={formData.parameters.durationInWeeks}
												onChange={(e) =>
													handleParameterChange(
														"durationInWeeks",
														parseInt(e.target.value),
													)
												}
												required
											/>
										</Grid>

										<Grid item xs={12} md={6}>
											<TextField
												fullWidth
												label="Max Yearly Earnings"
												type="number"
												value={formData.parameters.maxYearlyEarnings}
												onChange={(e) =>
													handleParameterChange(
														"maxYearlyEarnings",
														parseInt(e.target.value),
													)
												}
												required
											/>
										</Grid>
										<Grid item xs={12}>
											<BudgetEnforcementSection
												enabled={
													formData.parameters.budgetLimitEnabled ?? false
												}
												budgetLimit={formData.parameters.budgetLimit}
												threshold={formData.parameters.budgetLimitThreshold}
												mode={formData.parameters.budgetLimitEnforcementMode}
												consumption={budgetConsumption}
												onEnabledChange={(v) =>
													handleParameterChange("budgetLimitEnabled", v)
												}
												onBudgetLimitChange={(v) =>
													handleParameterChange("budgetLimit", v)
												}
												onThresholdChange={(v) =>
													handleParameterChange("budgetLimitThreshold", v)
												}
												onModeChange={(v) =>
													handleParameterChange("budgetLimitEnforcementMode", v)
												}
											/>
										</Grid>

										<Grid item xs={12} md={6}>
											<TextField
												fullWidth
												label="Base Incentive Negative"
												type="number"
												value={formData.parameters.baseIncentiveNegative}
												onChange={(e) =>
													handleParameterChange(
														"baseIncentiveNegative",
														parseFloat(e.target.value),
													)
												}
												required
											/>
										</Grid>
										<Grid item xs={12} md={6}>
											<TextField
												fullWidth
												label="Base Incentive Positive"
												type="number"
												value={formData.parameters.baseIncentivePositive}
												onChange={(e) =>
													handleParameterChange(
														"baseIncentivePositive",
														parseFloat(e.target.value),
													)
												}
												required
											/>
										</Grid>

										<Grid item xs={12} md={6}>
											<TextField
												fullWidth
												label="Bi-Visit Increment Negative"
												type="number"
												value={formData.parameters.biVisitIncrementNegative}
												onChange={(e) =>
													handleParameterChange(
														"biVisitIncrementNegative",
														parseFloat(e.target.value),
													)
												}
												required
											/>
										</Grid>
										<Grid item xs={12} md={6}>
											<TextField
												fullWidth
												label="Repetition Per Steps"
												type="number"
												value={formData.parameters.repetitionPerSteps}
												onChange={(e) =>
													handleParameterChange(
														"repetitionPerSteps",
														parseInt(e.target.value),
													)
												}
												required
											/>
										</Grid>
									</Grid>
								</CardContent>
							</Card>
						</Grid>

						<Grid item xs={12}>
							<Autocomplete
								multiple
								options={organizationsData || []}
								getOptionLabel={(option) => option.name}
								value={selectedOrgUnits}
								onChange={(_, newValue) => {
									setSelectedOrgUnits(newValue);
									setFormData({
										...formData,
										orgUnitId: newValue.map((org) => org._id),
									});
								}}
								renderInput={(params) => (
									<TextField
										{...params}
										label="Organization Units"
										placeholder="Select organization units"
									/>
								)}
								disabled={organizationsLoading}
							/>
						</Grid>

						<Grid item xs={12}>
							<Autocomplete
								multiple
								options={payersData || []}
								getOptionLabel={(option) => option.name}
								value={selectedPayers}
								onChange={(_, newValue) => {
									setSelectedPayers(newValue);
									setFormData({
										...formData,
										payers: newValue.map((payer) => payer._id),
									});
								}}
								renderInput={(params) => (
									<TextField
										{...params}
										label="Payer Plans"
										placeholder="Select payer plans that will be available for this program"
										helperText="Choose the active payer plans that beneficiaries can select during enrollment"
									/>
								)}
								disabled={payersLoading}
							/>
						</Grid>

						<Grid item xs={12} md={6}>
							<Autocomplete
								multiple
								options={rolesData}
								getOptionLabel={(option) => option.name}
								value={rolesData.filter((role) =>
									formData.cmRoles?.includes(role.id),
								)}
								onChange={(_, newValue) => {
									setFormData({
										...formData,
										cmRoles: newValue.map((role) => role.id),
									});
								}}
								renderInput={(params) => (
									<TextField
										{...params}
										label="Case Manager Roles"
										placeholder="Select case manager roles"
									/>
								)}
								disabled={rolesLoading}
							/>
						</Grid>

						<Grid item xs={12} md={6}>
							<Autocomplete
								multiple
								options={rolesData}
								getOptionLabel={(option) => option.name}
								value={rolesData.filter((role) =>
									formData.additionalStaffRoles?.includes(role.id),
								)}
								onChange={(_, newValue) => {
									setFormData({
										...formData,
										additionalStaffRoles: newValue.map((role) => role.id),
									});
								}}
								renderInput={(params) => (
									<TextField
										{...params}
										label="Additional Staff Roles"
										placeholder="Select additional staff roles"
									/>
								)}
								disabled={rolesLoading}
							/>
						</Grid>
						<Grid item xs={12}>
							<EducationalContentSection
								items={formData.educationalContent ?? []}
								newContentDays={
									formData.newContentDays ?? DEFAULT_NEW_CONTENT_DAYS
								}
								onItemsChange={handleEducationalContentChange}
								onNewContentDaysChange={handleNewContentDaysChange}
							/>
						</Grid>
						<Grid item xs={12}>
							<Box display="flex" gap={2} justifyContent="flex-end" mt={2}>
								<Button
									variant="outlined"
									onClick={() => {
										setShowCreateForm(false);
										setEditingProgramId(null);
										resetForm();
									}}
								>
									Cancel
								</Button>
								<Button
									variant="contained"
									onClick={handleSubmit}
									disabled={
										createMutation.isPending || updateMutation.isPending
									}
								>
									{editingProgramId
										? updateMutation.isPending
											? "Updating..."
											: "Update Program"
										: createMutation.isPending
											? "Creating..."
											: "Create Program"}
								</Button>
							</Box>
						</Grid>
					</Grid>
				</Paper>
			)}

			<Grid container spacing={3}>
				{isLoading
					? renderProgramSkeletons()
					: programs?.length === 0 && (
							<Grid item xs={12}>
								<Typography
									color="text.secondary"
									align="center"
									sx={{ py: 4 }}
								>
									No programs yet. Click &quot;Create New Program&quot; to add
									one.
								</Typography>
							</Grid>
						)}
				{!isLoading &&
					programs?.map((program: Program) => (
						<Grid item xs={12} md={6} lg={4} key={program._id}>
							<Card>
								<CardContent>
									<Box
										display="flex"
										justifyContent="space-between"
										alignItems="center"
										mb={2}
									>
										<Typography variant="h6">{program.name}</Typography>
										<Box display="flex" gap={1}>
											<Chip
												label={program.type || "Legacy"}
												color="primary"
												size="small"
											/>
											<Chip
												label={program.status}
												color={
													program.status === "ACTIVE" ? "success" : "default"
												}
												size="small"
											/>
											{program.budgetConsumption && (
												<BudgetBadge
													consumption={program.budgetConsumption}
													threshold={program.parameters.budgetLimitThreshold}
													mode={program.parameters.budgetLimitEnforcementMode}
												/>
											)}
											<Button
												size="small"
												variant="outlined"
												startIcon={<EditIcon />}
												onClick={() => handleEditProgram(program)}
											>
												Edit
											</Button>
										</Box>
									</Box>
									<Typography variant="body2" color="text.secondary" paragraph>
										Max Beneficiaries: {program.parameters.maxBeneficiaries}
									</Typography>
									<Typography variant="body2" color="text.secondary" paragraph>
										Duration: {program.parameters.durationInWeeks} weeks
									</Typography>
									{program.treatmentPlan && (
										<Typography
											variant="body2"
											color="text.secondary"
											paragraph
										>
											Treatment Plan:{" "}
											{getTreatmentPlanName(program.treatmentPlan)}
										</Typography>
									)}
									{program.orgUnitId && program.orgUnitId.length > 0 && (
										<Typography
											variant="body2"
											color="text.secondary"
											paragraph
										>
											Organization Units: {program.orgUnitId.length}
										</Typography>
									)}
									{program.payers && program.payers.length > 0 && (
										<Typography
											variant="body2"
											color="text.secondary"
											paragraph
										>
											Payer Plans: {program.payers.length}
										</Typography>
									)}
									<Typography variant="caption" display="block" gutterBottom>
										Created: {new Date(program.createdAt).toLocaleDateString()}
									</Typography>
									<Typography variant="caption" display="block">
										ID: {program._id}
									</Typography>
								</CardContent>
							</Card>
						</Grid>
					))}
			</Grid>
		</Box>
	);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function normalizeParameters(params: any) {
	const keysToNormalize = [
		"budgetLimit",
		"baseIncentiveNegative",
		"baseIncentivePositive",
		"biVisitIncrementNegative",
		"maxYearlyEarnings",
		"maxBeneficiaries",
		"durationInWeeks",
		"repetitionPerSteps",
	];
	const normalized = { ...params };
	for (const key of keysToNormalize) {
		if (
			normalized[key] &&
			typeof normalized[key] === "object" &&
			normalized[key].$numberDecimal
		) {
			normalized[key] = Number(normalized[key].$numberDecimal);
		}
	}
	return normalized;
}

export default ProgramsComponent;
